import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useAuth } from '@/lib/auth';
import { Logo } from '@/components/atoms/logo';
import { Button } from '@/components/atoms/button';
import { Micro } from '@/components/atoms/micro';
import { FormInput } from '@/components/molecules/form-input';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

const INSTRUMENTS = ['fiddle', 'pipes', 'whistle', 'flute', 'bodhrán', 'box', 'banjo'] as const;

const DUMMY_STATS = [
  { label: 'tunes', value: '47' },
  { label: 'day streak', value: '12' },
  { label: 'hours/week', value: '4.2' },
] as const;

function LoginPage(): ReactElement {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [instIdx, setInstIdx] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (): void => setReduceMotion(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (reduceMotion) return;
    const id = setInterval(() => setInstIdx((i) => (i + 1) % INSTRUMENTS.length), 2200);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, name);
      } else {
        await login(email, password);
      }
      await navigate({ to: '/' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = (): void => {
    setIsRegister((prev) => !prev);
    setError('');
  };

  const submitLabel = isRegister ? 'Create account' : 'Sign in';

  return (
    <div className="min-h-screen bg-[var(--bg-page)] flex flex-col md:flex-row">
      {/* Left panel — desktop/tablet only */}
      <div className="hidden md:flex flex-1 flex-col justify-between bg-[var(--ink)] text-white p-12 overflow-hidden">
        <Logo size={28} className="[&_span]:text-white [&_svg_rect]:fill-white [&_svg_path]:stroke-[var(--ink)] [&_svg_circle:last-child]:fill-[var(--warm)]" />

        <div className="max-w-sm">
          <h1 className="font-display text-[48px] font-semibold leading-[1.1] tracking-tight text-white">
            A quiet book for noisy music.
          </h1>
          <p className="mt-4 text-[15px] text-white/70 leading-relaxed">
            Track your tunes, your streak, and where you left off. Built for session musicians who never stop learning.
          </p>

          <div className="grid grid-cols-3 gap-4 mt-10">
            {DUMMY_STATS.map(({ value, label }) => (
              <div key={label} className="flex flex-col">
                <span className="font-display text-[32px] font-semibold text-white leading-none">{value}</span>
                <span className="text-[12px] text-white/50 mt-1">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[11px] text-white/30 mt-8">
          Stats shown are illustrative placeholders — your real data waits inside.
        </p>
      </div>

      {/* Right form column */}
      <div className="flex-1 md:w-[480px] md:max-w-[480px] md:flex-none flex flex-col bg-[var(--bg-surface)]">
        {/* Mobile top row: logo */}
        <div className="md:hidden px-5 pt-4 flex items-center">
          <Logo />
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 md:px-14 py-8">
          {/* Mobile: instrument-cycling headline */}
          <div className="md:hidden mb-6">
            <Micro>Welcome back</Micro>
            <p className="font-display text-[28px] font-semibold tracking-tight leading-tight text-[var(--ink)] mt-2">
              Pick up your{' '}
              <span
                key={instIdx}
                style={
                  reduceMotion
                    ? { display: 'inline-block' }
                    : { display: 'inline-block', animation: 'instFade 0.4s ease forwards' }
                }
              >
                {INSTRUMENTS[instIdx]}
              </span>{' '}
              again.
            </p>
            <p className="text-[13px] text-[var(--ink-3)] mt-2 leading-relaxed">
              Sign in to find your tunes, your streak, and where you left off.
            </p>
          </div>

          {/* Desktop heading */}
          <div className="hidden md:block">
            <Micro>Welcome back</Micro>
            <h2 className="font-display text-[32px] font-semibold tracking-tight mt-2 leading-tight text-[var(--ink)]">
              {isRegister ? 'Create account' : 'Sign in'}
            </h2>
            <p className="text-[13px] text-[var(--ink-3)] mt-2">
              {isRegister
                ? 'Join musicians managing their repertoire.'
                : 'Pick up where you left off.'}
            </p>
          </div>

          <form className="mt-8 space-y-3" onSubmit={(e) => { void handleSubmit(e); }}>
            {isRegister && (
              <FormInput
                id="name"
                label="Name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                disabled={loading}
              />
            )}

            <FormInput
              id="email"
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={loading}
            />

            <FormInput
              id="password"
              label="Password"
              type="password"
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={loading}
              showPasswordToggle
            />

            {error && (
              <p role="alert" className="text-[12px] text-[var(--err)]">
                {error}
              </p>
            )}

            <Button type="submit" variant="primary" size="lg" fullWidth disabled={loading}>
              {loading ? 'Please wait…' : submitLabel}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[var(--rule)]" />
            <span className="text-[10px] font-semibold text-[var(--ink-3)] uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-[var(--rule)]" />
          </div>

          {/* TODO(BACKLOG): wire up social auth — Google and Apple are disabled until OAuth is configured */}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="secondary" icon="google" disabled>Google</Button>
            <Button variant="secondary" icon="apple" disabled>Apple</Button>
          </div>

          <div className="text-center text-[12px] text-[var(--ink-3)] mt-8">
            {isRegister ? 'Already have an account?' : 'New here?'}{' '}
            <button
              type="button"
              onClick={toggleMode}
              className="font-semibold text-[var(--ink)]"
            >
              {isRegister ? 'Back to sign in' : 'Create account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
